const CRITICAL_TARGET_STATUSES = new Set([
  'ready_for_collection',
  'completed',
  'cancelled',
  'voided',
  'closed',
  'collected',
])

const CLOSED_STATUSES = new Set(['completed', 'closed', 'collected'])
const OWNER_ONLY_ACTIONS = new Set(['manual_paid', 'close', 'reopen', 'cancel', 'administrative_close'])
const OWNER_ONLY_STATUSES = new Set(['completed', 'cancelled', 'voided', 'closed', 'collected'])

function clean(value) {
  return String(value || '').trim()
}

export function normaliseLifecycleStatus(value) {
  const status = clean(value)
  return status || 'received'
}

export function lifecycleActionForStatusChange(previousStatus, nextStatus) {
  const previous = normaliseLifecycleStatus(previousStatus)
  const next = normaliseLifecycleStatus(nextStatus)
  if (previous === next) return null
  if (next === 'ready_for_collection') return 'ready'
  if (next === 'completed' || next === 'closed' || next === 'collected') return 'close'
  if (next === 'cancelled' || next === 'voided') return 'cancel'
  if (CLOSED_STATUSES.has(previous) && !CLOSED_STATUSES.has(next)) return 'reopen'
  return null
}

export function shouldUseTrustedLifecycleAction(previousStatus, nextStatus) {
  return Boolean(lifecycleActionForStatusChange(previousStatus, nextStatus))
}

export function statusForDirectJobSave(previousStatus, nextStatus, fallback = 'received') {
  return shouldUseTrustedLifecycleAction(previousStatus, nextStatus)
    ? normaliseLifecycleStatus(previousStatus || fallback)
    : normaliseLifecycleStatus(nextStatus || fallback)
}

export function lifecycleNoteForAction(action, job = {}, fallback = '') {
  const totalPence = Math.round(Math.max(0, Number(job.total_charged || 0)) * 100)
  if (action === 'ready') return fallback || 'Job marked ready for collection.'
  if (action === 'cancel') return fallback || 'Job cancelled from the job card.'
  if (action === 'reopen') return fallback || 'Job reopened from the job card.'
  if (action === 'close' && totalPence <= 0) return fallback || 'No-charge job completed and closed from the job card.'
  if (action === 'close') return fallback || 'Job completed and closed from the job card.'
  return fallback || 'Job lifecycle updated.'
}

export function lifecycleActionRequiresOwner(action) {
  return OWNER_ONLY_ACTIONS.has(clean(action))
}

export function canRunOwnerLifecycleActions(profile = {}) {
  return Boolean(profile?.is_superadmin || clean(profile?.role) === 'owner')
}

export function canOverrideScheduleCapacity(profile = {}) {
  return canRunOwnerLifecycleActions(profile)
}

export function canRunJobLifecycleAction(profile = {}, action = '') {
  if (lifecycleActionRequiresOwner(action)) return canRunOwnerLifecycleActions(profile)
  return Boolean(profile?.is_superadmin || ['owner', 'technician'].includes(clean(profile?.role)))
}

export function filterLifecycleStatusOptionsForProfile(options = [], profile = {}, currentStatus = '') {
  if (canRunOwnerLifecycleActions(profile)) return options
  const current = normaliseLifecycleStatus(currentStatus)
  return options.filter(([value]) => {
    const status = normaliseLifecycleStatus(value)
    return status === current || !OWNER_ONLY_STATUSES.has(status)
  })
}

export async function runJobLifecycleAction(supabase, action, jobId, options = {}) {
  if (!supabase?.rpc) throw new Error('Supabase client is required.')
  if (!clean(jobId)) throw new Error('Job id is required.')

  const note = clean(options.note)
  const reason = clean(options.reason)

  const rpcMap = {
    ready: ['mark_service_job_ready_for_collection', { p_job_id: jobId, p_note: note }],
    manual_paid: ['mark_service_job_paid_manual', {
      p_job_id: jobId,
      p_method: clean(options.method || 'manual'),
      p_reference: clean(options.reference),
      p_amount_pence: Number(options.amountPence || 0),
      p_note: note,
    }],
    close: ['close_service_job', { p_job_id: jobId, p_note: note }],
    administrative_close: ['administratively_close_paid_service_job', {
      p_job_id: jobId,
      p_reason: reason,
      p_confirm_incomplete: options.confirmIncomplete === true,
    }],
    reopen: ['reopen_service_job', { p_job_id: jobId, p_note: note }],
    cancel: ['cancel_service_job', { p_job_id: jobId, p_reason: reason || note }],
  }

  const [rpcName, params] = rpcMap[action] || []
  if (!rpcName) throw new Error(`Unknown lifecycle action: ${action}`)

  const { data, error } = await supabase.rpc(rpcName, params)
  if (error) throw error
  return data || {}
}

export async function runJobScheduleUpdate(supabase, jobId, options = {}) {
  if (!supabase?.rpc) throw new Error('Supabase client is required.')
  if (!clean(jobId)) throw new Error('Job id is required.')

  const scheduledDate = clean(options.scheduledDate ?? options.date)
  const { data, error } = await supabase.rpc('update_service_job_schedule', {
    p_job_id: jobId,
    p_date: scheduledDate || null,
    p_technician_id: clean(options.technicianId) || null,
    p_scheduled_duration_mins: null,
    p_override_over_capacity: options.overrideOverCapacity === true,
    p_note: clean(options.note),
  })
  if (error) throw error
  return data || {}
}

export async function runServiceJobCreateWithSchedule(supabase, jobPayload = {}, options = {}) {
  if (!supabase?.rpc) throw new Error('Supabase client is required.')
  if (!jobPayload || typeof jobPayload !== 'object') throw new Error('Job payload is required.')

  const scheduledDate = clean(options.scheduledDate ?? options.date)
  const params = {
    p_job_payload: jobPayload,
    p_date: scheduledDate || null,
    p_technician_id: clean(options.technicianId) || null,
    p_scheduled_duration_mins: null,
    p_override_over_capacity: options.overrideOverCapacity === true,
    p_note: clean(options.note),
  }
  if (options.vatPrices) {
    if (!options.vatRequestRef) throw new Error('Reviewed intake request identity is required.')
    const reviewed = { ...params, p_job_payload: { ...jobPayload, vat_price_review: options.vatPrices } }
    const fingerprint = JSON.stringify(reviewed)
    if (options.vatRequestRef.current?.fingerprint !== fingerprint) {
      options.vatRequestRef.current = { fingerprint, key: crypto.randomUUID() }
    }
    params.p_job_payload = { ...reviewed.p_job_payload, vat_intake_key: options.vatRequestRef.current.key }
  }
  const { data, error } = await supabase.rpc('create_service_job_with_schedule', params)
  if (error) throw error
  return data || {}
}

export async function runCapacitySettingsUpdate(supabase, workshopId, options = {}) {
  if (!supabase?.rpc) throw new Error('Supabase client is required.')
  if (!clean(workshopId)) throw new Error('Workshop id is required.')

  const weeklyHours = Math.max(0, Number(options.weeklyBookableHours ?? 30) || 0)
  const technicians = Array.isArray(options.technicians) ? options.technicians : []
  const { data, error } = await supabase.rpc('update_workshop_capacity_settings', {
    p_workshop_id: workshopId,
    p_weekly_bookable_hours: weeklyHours,
    p_technician_settings: technicians.map(member => ({
      id: clean(member.id),
      weekly_bookable_hours: member.weekly_bookable_hours === '' || member.weekly_bookable_hours === null || member.weekly_bookable_hours === undefined
        ? null
        : Math.max(0, Number(member.weekly_bookable_hours) || 0),
      calendar_colour: clean(member.calendar_colour),
    })),
  })
  if (error) throw error
  return data || {}
}

export function isCriticalLifecycleTarget(status) {
  return CRITICAL_TARGET_STATUSES.has(normaliseLifecycleStatus(status))
}
